import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, Save, Plus, Trash2, X, Image as ImageIcon,
    Volume2, Lightbulb, Package, Settings2, Monitor, Layers,
    Cpu, Wrench, Star, Zap, Box, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api, BASE, upload3dFile } from "../../services/api";
import ImageUpload from "../../components/ui/ImageUpload";

// ── Constants ─────────────────────────────────────────────────────────────────
const ICON_MAP = { Volume2, Lightbulb, Package, Settings2, Monitor, Layers, Cpu, Wrench, Star, Zap };

const ICON_OPTIONS = [
    { name: "Volume2",   label: "Zvuk"      },
    { name: "Lightbulb", label: "Svetlo"    },
    { name: "Package",   label: "Balík"     },
    { name: "Settings2", label: "Nastavenia"},
    { name: "Monitor",   label: "Monitor"   },
    { name: "Layers",    label: "Vrstvy"    },
    { name: "Cpu",       label: "Čip"       },
    { name: "Wrench",    label: "Kľúč"      },
    { name: "Star",      label: "Hviezda"   },
    { name: "Zap",       label: "Blesk"     },
];

const ACCENT_PRESETS = [
    "#0099FF", "#a855f7", "#22c55e", "#f59e0b",
    "#ec4899", "#f97316", "#14b8a6", "#f43f5e",
];

const TABS = [
    { id: "basic",     label: "Základné"   },
    { id: "visual",    label: "Vizuál"     },
    { id: "photos",    label: "Fotky"      },
    { id: "features",  label: "Funkcie"    },
    { id: "usecases",  label: "Využitie"   },
    { id: "equipment", label: "Zariadenia" },
    { id: "stats",     label: "Štatistiky" },
    { id: "model3d",   label: "3D Modely"  },
];

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Sub-editors ───────────────────────────────────────────────────────────────
function FeaturesList({ items, onChange }) {
    const update = (i, field, val) =>
        onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { title: "", desc: "" }]);

    return (
        <div className="space-y-3">
            {items.map((it, i) => (
                <div key={i} className="bg-nova-black/40 border border-white/8 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-zinc-600 w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <input
                            value={it.title}
                            onChange={e => update(i, "title", e.target.value)}
                            placeholder="Názov funkcie"
                            className="flex-1 bg-transparent text-white text-sm font-medium border-b border-white/10 focus:border-nova-cyan/50 outline-none pb-1 transition-colors"
                        />
                        <button onClick={() => remove(i)} className="text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <textarea
                        value={it.desc}
                        onChange={e => update(i, "desc", e.target.value)}
                        placeholder="Popis funkcie…"
                        rows={2}
                        className="w-full bg-transparent text-zinc-400 text-sm resize-none outline-none leading-relaxed pl-7 border-b border-white/5 focus:border-white/15 transition-colors"
                    />
                </div>
            ))}
            <button onClick={add} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center">
                <Plus className="w-3.5 h-3.5" /> Pridať funkciu
            </button>
        </div>
    );
}

function UseCasesList({ items, onChange }) {
    const update = (i, val) => onChange(items.map((it, idx) => (idx === i ? val : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, ""]);

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap gap-2 mb-3">
                {items.map((uc, i) => (
                    <div key={i} className="group flex items-center gap-1.5 bg-nova-surface border border-white/10 rounded-full px-3 py-1.5">
                        <input
                            value={uc}
                            onChange={e => update(i, e.target.value)}
                            className="bg-transparent text-sm text-white outline-none min-w-[80px] max-w-[200px]"
                            style={{ width: `${Math.max(uc.length * 8, 80)}px` }}
                        />
                        <button onClick={() => remove(i)} className="text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={add} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center">
                <Plus className="w-3.5 h-3.5" /> Pridať využitie
            </button>
        </div>
    );
}

function EquipmentList({ items, onChange }) {
    const update = (i, field, val) =>
        onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { brand: "", model: "", detail: "" }]);

    return (
        <div className="space-y-3">
            {items.map((it, i) => (
                <div key={i} className="bg-nova-black/40 border border-white/8 rounded-xl p-4 grid grid-cols-3 gap-3 items-end">
                    <div>
                        <label className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1 block">Výrobca</label>
                        <input value={it.brand} onChange={e => update(i, "brand", e.target.value)}
                            className="w-full bg-nova-surface/50 border border-white/8 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-nova-cyan/40"
                            placeholder="napr. Robe" />
                    </div>
                    <div>
                        <label className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1 block">Model</label>
                        <input value={it.model} onChange={e => update(i, "model", e.target.value)}
                            className="w-full bg-nova-surface/50 border border-white/8 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-nova-cyan/40"
                            placeholder="napr. BMFL Blade" />
                    </div>
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1 block">Detail</label>
                            <input value={it.detail} onChange={e => update(i, "detail", e.target.value)}
                                className="w-full bg-nova-surface/50 border border-white/8 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-nova-cyan/40"
                                placeholder="napr. 174 000 lm" />
                        </div>
                        <button onClick={() => remove(i)} className="mt-5 text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            ))}
            <button onClick={add} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center">
                <Plus className="w-3.5 h-3.5" /> Pridať zariadenie
            </button>
        </div>
    );
}

function StatsList({ items, onChange }) {
    const update = (i, field, val) =>
        onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { val: "", label: "" }]);

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {items.map((it, i) => (
                    <div key={i} className="bg-nova-black/40 border border-white/8 rounded-xl p-4 relative group">
                        <button onClick={() => remove(i)}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all">
                            <X className="w-3 h-3" />
                        </button>
                        <input value={it.val} onChange={e => update(i, "val", e.target.value)}
                            placeholder="napr. 140 dB"
                            className="w-full bg-transparent font-heading text-2xl font-black text-white outline-none mb-1" />
                        <input value={it.label} onChange={e => update(i, "label", e.target.value)}
                            placeholder="popis"
                            className="w-full bg-transparent font-mono text-[10px] uppercase tracking-wider text-zinc-500 outline-none" />
                    </div>
                ))}
            </div>
            <button onClick={add} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center">
                <Plus className="w-3.5 h-3.5" /> Pridať štatistiku
            </button>
        </div>
    );
}

function ImagesManager({ heroImage, images, onHeroChange, onImagesChange }) {
    const addImage = (url) => onImagesChange([...images, url]);
    const removeImage = (i) => onImagesChange(images.filter((_, idx) => idx !== i));
    const updateImage = (i, val) => onImagesChange(images.map((img, idx) => (idx === i ? val : img)));

    return (
        <div className="space-y-8">
            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3 block">Hlavná fotka (hero)</label>
                <ImageUpload value={heroImage} onChange={onHeroChange} />
            </div>
            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3 block">
                    Galéria fotiek ({images.length})
                </label>
                <div className="space-y-4">
                    {images.map((img, i) => (
                        <div key={i} className="flex items-start gap-3">
                            <div className="w-24 h-16 rounded-lg overflow-hidden shrink-0 border border-white/8 bg-nova-surface/30">
                                {img ? (
                                    <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <ImageIcon className="w-4 h-4 text-zinc-600" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <ImageUpload value={img} onChange={val => updateImage(i, val)} compact />
                            </div>
                            <button onClick={() => removeImage(i)} className="mt-1 text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                    <button onClick={() => addImage("")} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center">
                        <Plus className="w-3.5 h-3.5" /> Pridať fotku
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── 3D Models list ────────────────────────────────────────────────────────────
function SingleModel3d({ item, index, onChange, onRemove }) {
    const [uploading, setUploading] = React.useState(false);
    const inputRef = React.useRef(null);

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const { url: uploaded } = await upload3dFile(file);
            onChange("url", uploaded);
            toast.success("Model nahraný");
        } catch (e) {
            toast.error(e.message || "Upload zlyhal");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bg-nova-black/40 border border-white/8 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                    3D Model {String(index + 1).padStart(2, "0")}
                </span>
                <button type="button" onClick={onRemove} className="text-zinc-600 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>

            <input
                value={item.title}
                onChange={e => onChange("title", e.target.value)}
                placeholder="Názov (napr. Pódium 12×8 m)"
                className="w-full h-10 px-3 rounded-lg bg-nova-black/60 border border-white/10 text-white text-sm outline-none focus:border-nova-cyan/40"
            />
            <textarea
                value={item.desc}
                onChange={e => onChange("desc", e.target.value)}
                placeholder="Krátky popis 3D modelu…"
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-nova-black/60 border border-white/10 text-zinc-300 text-sm resize-none outline-none focus:border-nova-cyan/40 leading-relaxed"
            />

            <div className="flex gap-2 items-center">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-nova-surface/40 text-white text-xs font-medium hover:bg-white/5 transition-colors disabled:opacity-50 shrink-0"
                >
                    {uploading
                        ? <><div className="w-3.5 h-3.5 border-2 border-nova-cyan/30 border-t-nova-cyan rounded-full animate-spin" /> Nahrávam…</>
                        : <><Upload className="w-3.5 h-3.5 text-nova-cyan" /> Nahrať .glb</>
                    }
                </button>
                <input ref={inputRef} type="file" accept=".glb,.gltf,.obj" className="hidden"
                    onChange={e => handleFile(e.target.files[0])} />
                <input
                    value={item.url}
                    onChange={e => onChange("url", e.target.value)}
                    placeholder="alebo URL súboru…"
                    className="flex-1 h-9 px-3 rounded-lg bg-nova-black/60 border border-white/10 text-white text-xs outline-none focus:border-nova-cyan/40 font-mono"
                />
            </div>

            {item.url && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-nova-cyan/5 border border-nova-cyan/15">
                    <Box className="w-3.5 h-3.5 text-nova-cyan shrink-0" />
                    <span className="text-nova-cyan/70 text-xs font-mono truncate">{item.url}</span>
                </div>
            )}
        </div>
    );
}

function Models3dList({ items, onChange, legacyUrl, onClearLegacy }) {
    const update = (i, field, val) =>
        onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { title: "", desc: "", url: "" }]);

    return (
        <div className="space-y-4 max-w-2xl">
            <p className="text-zinc-500 text-xs leading-relaxed">
                Každý 3D model sa zobrazí pod sekciou „Čo dostanete" s vlastným názvom a popisom.
            </p>

            {/* Legacy model3d_url warning */}
            {legacyUrl && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-500/25 bg-amber-500/8">
                    <Box className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="text-amber-300 text-xs font-medium mb-0.5">Starý 3D model (jednoduchý)</div>
                        <div className="text-zinc-500 text-xs font-mono truncate">{legacyUrl}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClearLegacy}
                        className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/15 text-red-400 text-xs hover:bg-red-500/25 transition-colors"
                    >
                        <Trash2 className="w-3 h-3" /> Odstrániť
                    </button>
                </div>
            )}

            {items.length === 0 && !legacyUrl && (
                <div className="px-4 py-3 rounded-xl border border-white/5 bg-nova-surface/20 text-zinc-600 text-xs font-mono">
                    Žiadne 3D modely — sekcia sa nebude zobrazovať.
                </div>
            )}
            {items.map((item, i) => (
                <SingleModel3d
                    key={i}
                    item={item}
                    index={i}
                    onChange={(field, val) => update(i, field, val)}
                    onRemove={() => remove(i)}
                />
            ))}
            <button
                type="button"
                onClick={add}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center"
            >
                <Plus className="w-3.5 h-3.5" /> Pridať 3D model
            </button>
        </div>
    );
}

// ── Main edit page ────────────────────────────────────────────────────────────
export default function AdminSluzbaEdit() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("basic");
    const [dirty, setDirty] = useState(false);

    const set = useCallback((field, val) => {
        setForm(f => ({ ...f, [field]: val }));
        setDirty(true);
    }, []);

    useEffect(() => {
        setLoading(true);
        api.getSluzba(slug)
            .then(data => { setForm(data); setLoading(false); })
            .catch(() => { toast.error("Služba nenájdená"); navigate("/admin/sluzby"); });
    }, [slug, navigate]);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        try {
            const payload = {
                title:       form.title,
                tagline:     form.tagline,
                desc:        form.desc,
                short_desc:  form.short_desc || "",
                hero_image:  form.hero_image,
                images:      form.images,
                features:    form.features,
                useCases:    form.useCases,
                equipment:   form.equipment,
                stats:       form.stats,
                gallery:     form.gallery,
                icon_name:   form.icon_name,
                accent:      form.accent,
                order:       form.order ?? 99,
                model3d_url: form.model3d_url || "",
                models3d:    form.models3d    || [],
            };
            const updated = await api.updateSluzba(slug, payload);
            setForm(updated);
            setDirty(false);
            toast.success("Uložené");
        } catch (e) {
            toast.error(e.message || "Chyba pri ukladaní");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !form) {
        return (
            <div className="space-y-4 animate-pulse">
                <div className="h-14 bg-nova-surface/40 rounded-xl" />
                <div className="h-10 bg-nova-surface/30 rounded-xl" />
                <div className="h-64 bg-nova-surface/20 rounded-xl" />
            </div>
        );
    }

    const Icon = ICON_MAP[form.icon_name] || Layers;
    const accent = form.accent || "#0099ff";

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
        >
            {/* ── Page header ── */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate("/admin/sluzby")}
                    className="flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors text-sm font-medium"
                >
                    <ArrowLeft className="w-4 h-4" /> Späť
                </button>

                <div className="h-5 w-px bg-white/10" />

                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: `${accent}14`, border: `1px solid ${accent}33` }}>
                        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.6} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="font-heading font-black text-white text-lg leading-none truncate">{form.title}</h1>
                        <div className="font-mono text-[10px] text-zinc-500 mt-0.5">/sluzby/{slug}</div>
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {dirty && <span className="font-mono text-[10px] text-amber-400/70">Neuložené zmeny</span>}
                    <button
                        onClick={save}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 disabled:opacity-50 transition-colors"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? "Ukladám…" : "Uložiť"}
                    </button>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="flex gap-0.5 border-b border-white/8 mb-8 overflow-x-auto">
                {TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                            tab === t.id
                                ? "text-white border-nova-cyan"
                                : "text-zinc-500 border-transparent hover:text-zinc-300"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── Tab content ── */}
            <div>
                {tab === "basic" && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Názov</label>
                                <input
                                    value={form.title || ""}
                                    onChange={e => set("title", e.target.value)}
                                    className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-white outline-none focus:border-nova-cyan/40 font-heading font-bold text-xl"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">
                                    Krátky popis <span className="text-zinc-600 normal-case font-sans">(karta na úvodnej stránke)</span>
                                </label>
                                <textarea
                                    value={form.short_desc || ""}
                                    onChange={e => set("short_desc", e.target.value)}
                                    rows={2}
                                    placeholder="Stručný popis pre kartu…"
                                    className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-zinc-300 outline-none focus:border-nova-cyan/40 resize-none leading-relaxed text-sm"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Tagline</label>
                                <input
                                    value={form.tagline || ""}
                                    onChange={e => set("tagline", e.target.value)}
                                    placeholder="Krátky výrazný podtitulek…"
                                    className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 outline-none focus:border-nova-cyan/40 font-medium"
                                    style={{ color: accent }}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Detailný popis</label>
                                <textarea
                                    value={form.desc || ""}
                                    onChange={e => set("desc", e.target.value)}
                                    rows={6}
                                    placeholder="Dlhší popis služby zobrazený na detail stránke…"
                                    className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-zinc-300 outline-none focus:border-nova-cyan/40 resize-none leading-relaxed"
                                />
                            </div>

                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">
                                    Kategória referencií
                                </label>
                                <input
                                    value={form.gallery || ""}
                                    onChange={e => set("gallery", e.target.value)}
                                    placeholder="napr. ozvucenie, osvetlenie, led…"
                                    className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-white outline-none focus:border-nova-cyan/40 font-mono text-sm"
                                />
                                <div className="text-zinc-600 text-xs mt-1.5">Zobrazia sa referencie s touto kategóriou na stránke služby</div>
                            </div>

                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Poradie</label>
                                <input
                                    type="number"
                                    value={form.order ?? 99}
                                    onChange={e => set("order", parseInt(e.target.value) || 99)}
                                    className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-white outline-none focus:border-nova-cyan/40 font-mono"
                                />
                                <div className="text-zinc-600 text-xs mt-1.5">Nižšie číslo = zobrazí sa skôr (1 = prvá)</div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "visual" && (
                    <div className="space-y-8">
                        {/* Live preview card */}
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3 block">Náhľad karty</label>
                            <div className="inline-flex items-center gap-4 p-5 rounded-2xl border"
                                style={{ background: `${accent}0d`, borderColor: `${accent}30` }}>
                                <div className="w-14 h-14 rounded-xl flex items-center justify-center"
                                    style={{ background: `${accent}14`, border: `1px solid ${accent}40` }}>
                                    <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={1.6} />
                                </div>
                                <div>
                                    <div className="font-heading font-bold text-white text-base">{form.title}</div>
                                    <div className="text-zinc-400 text-sm mt-0.5 max-w-xs">{form.short_desc || form.desc?.slice(0, 80) || "…"}</div>
                                </div>
                            </div>
                        </div>

                        {/* Icon picker */}
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3 block">Ikona</label>
                            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                                {ICON_OPTIONS.map(opt => {
                                    const Ic = ICON_MAP[opt.name];
                                    return (
                                        <button key={opt.name} type="button"
                                            onClick={() => set("icon_name", opt.name)}
                                            className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${
                                                form.icon_name === opt.name
                                                    ? "border-nova-cyan/60 bg-nova-cyan/10 text-nova-cyan"
                                                    : "border-white/8 text-zinc-500 hover:border-white/20 hover:text-white bg-nova-black/30"
                                            }`}>
                                            <Ic className="w-5 h-5" strokeWidth={1.6} />
                                            <span className="font-mono text-[9px]">{opt.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Accent color */}
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-3 block">Farba akcentu</label>
                            <div className="flex items-center gap-3 flex-wrap mb-3">
                                {ACCENT_PRESETS.map(c => (
                                    <button key={c} type="button" onClick={() => set("accent", c)}
                                        className={`w-9 h-9 rounded-full border-2 transition-all ${form.accent === c ? "border-white scale-110 shadow-lg" : "border-transparent hover:scale-105"}`}
                                        style={{ background: c }} />
                                ))}
                                <div className="flex items-center gap-2 ml-2">
                                    <input
                                        type="color"
                                        value={form.accent || "#0099ff"}
                                        onChange={e => set("accent", e.target.value)}
                                        className="w-9 h-9 rounded-full cursor-pointer bg-transparent border-0 p-0"
                                        title="Vlastná farba"
                                    />
                                    <span className="font-mono text-xs text-zinc-500">{form.accent}</span>
                                </div>
                            </div>

                            {/* Color preview strips */}
                            <div className="grid grid-cols-3 gap-3 mt-4">
                                <div className="rounded-lg p-3 text-center" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
                                    <div className="font-mono text-[9px] text-zinc-500 mb-1">Pozadie</div>
                                    <div className="w-4 h-4 rounded-full mx-auto" style={{ background: `${accent}14` }} />
                                </div>
                                <div className="rounded-lg p-3 text-center" style={{ background: `${accent}22`, border: `1px solid ${accent}50` }}>
                                    <div className="font-mono text-[9px] text-zinc-500 mb-1">Ohraničenie</div>
                                    <div className="w-4 h-4 rounded-full mx-auto" style={{ background: accent }} />
                                </div>
                                <div className="rounded-lg p-3 text-center" style={{ background: accent }}>
                                    <div className="font-mono text-[9px] text-black/60 mb-1">CTA tlačidlo</div>
                                    <div className="w-4 h-4 rounded-full mx-auto bg-black/20" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "photos" && (
                    <ImagesManager
                        heroImage={form.hero_image || ""}
                        images={form.images || []}
                        onHeroChange={val => set("hero_image", val)}
                        onImagesChange={val => set("images", val)}
                    />
                )}

                {tab === "features" && (
                    <FeaturesList items={form.features || []} onChange={val => set("features", val)} />
                )}

                {tab === "usecases" && (
                    <UseCasesList items={form.useCases || []} onChange={val => set("useCases", val)} />
                )}

                {tab === "equipment" && (
                    <EquipmentList items={form.equipment || []} onChange={val => set("equipment", val)} />
                )}

                {tab === "stats" && (
                    <StatsList items={form.stats || []} onChange={val => set("stats", val)} />
                )}

                {tab === "model3d" && (
                    <Models3dList
                        items={form.models3d || []}
                        onChange={val => set("models3d", val)}
                        legacyUrl={form.model3d_url || ""}
                        onClearLegacy={() => set("model3d_url", "")}
                    />
                )}
            </div>

            {/* ── Floating save (mobile) ── */}
            <div className="fixed bottom-6 right-6 lg:hidden z-40">
                <button
                    onClick={save}
                    disabled={saving || !dirty}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm shadow-xl disabled:opacity-40 transition-all"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Ukladám…" : "Uložiť"}
                </button>
            </div>
        </motion.div>
    );
}
